import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, History, Loader2 } from "lucide-react";
import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export function CompetitorHistoryPanel({ siteId, refreshKey, currentReportId, onViewReport }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get(`/sites/${siteId}/competitor-analysis/history`);
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      setEntries([]);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const namesOf = (e) => e.analyzed_competitors || e.competitors_crawled || [];
  const names = [...new Set(entries.flatMap(namesOf))].sort();
  const filtered = filter ? entries.filter((e) => namesOf(e).includes(filter)) : entries;
  const duelPoints = filter
    ? filtered
        .filter((e) => e.duel_mode && typeof e.victory_score === "number")
        .map((e) => ({
          date: new Date(e.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
          score: e.victory_score,
          ts: e.created_at,
        }))
        .sort((a, b) => a.ts.localeCompare(b.ts))
    : [];

  const view = async (id) => {
    setViewing(id);
    try {
      const { data } = await api.get(`/sites/${siteId}/competitor-analysis/report/${id}`);
      onViewReport(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Impossible de charger ce rapport.");
    } finally {
      setViewing(null);
    }
  };

  if (entries.length < 2) return null;

  return (
    <div className="border border-slate-200 bg-white rounded-lg mb-6" data-testid="competitor-history-panel">
      <button
        onClick={() => setOpen(!open)}
        data-testid="history-toggle"
        className="w-full flex items-center justify-between px-5 py-3.5 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <History className="w-4 h-4 text-[#002FA7]" /> Historique des analyses
          <span className="text-xs font-normal text-slate-500">({entries.length})</span>
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-4">
          {names.length > 1 && (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <label className="text-xs text-slate-500">Évolution face à :</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="history-competitor-filter"
                className="border border-slate-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#002FA7]"
              >
                <option value="">Tous les concurrents</option>
                {names.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {filter && (
                <span className="text-[11px] text-slate-500" data-testid="history-filter-count">
                  {filtered.length} analyse{filtered.length > 1 ? "s" : ""} face à {filter}
                </span>
              )}
            </div>
          )}
          {filter && duelPoints.length >= 2 && (
            <div className="mb-3 border border-slate-100 rounded-md p-3" data-testid="duel-score-chart">
              <div className="text-[11px] font-semibold text-slate-700 mb-1">
                Progression du score de victoire face à {filter} ({duelPoints.length} duels)
              </div>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={duelPoints} margin={{ top: 8, right: 12, left: -22, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => [`${v}/100`, "Score de victoire"]} />
                    <ReferenceLine y={50} stroke="#94a3b8" strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="score" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {filtered.map((e, i) => (
              <div
                key={e.id}
                data-testid={`history-entry-${i}`}
                className={`flex items-center gap-3 border rounded-md px-3 py-2 ${
                  currentReportId === e.id ? "border-[#002FA7] bg-blue-50/40" : "border-slate-200"
                }`}
              >
                <span className="text-xs text-slate-500 w-28 flex-shrink-0">
                  {new Date(e.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                </span>
                {e.duel_mode ? (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 flex-shrink-0">⚔️ Duel</span>
                ) : (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex-shrink-0">
                    Multi{namesOf(e).length ? ` (${namesOf(e).length})` : ""}
                  </span>
                )}
                {e.duel_mode && typeof e.victory_score === "number" && (
                  <span
                    data-testid={`history-score-${i}`}
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
                      e.victory_score >= 60 ? "bg-green-50 text-green-700" : e.victory_score >= 40 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"
                    }`}
                  >
                    {e.victory_score}/100
                  </span>
                )}
                <span className="text-xs text-slate-700 truncate flex-1">{namesOf(e).join(", ") || "—"}</span>
                <button
                  onClick={() => view(e.id)}
                  disabled={viewing === e.id || currentReportId === e.id}
                  data-testid={`history-view-${i}`}
                  className="text-xs font-medium text-[#002FA7] hover:underline disabled:text-slate-400 disabled:no-underline flex-shrink-0"
                >
                  {viewing === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : currentReportId === e.id ? "Affiché" : "Voir"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
